import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { CrmAPI, CrmCard, Venda } from '../services/api';

const COLUMNS: { id: string; label: string; emoji: string }[] = [
  { id: 'lead_novo', label: 'Lead Novo',  emoji: '👤' },
  { id: 'fase1',     label: 'Fase 1',     emoji: '🤖' },
  { id: 'fase2',     label: 'Fase 2',     emoji: '🎧' },
  { id: 'fase3',     label: 'Fase 3',     emoji: '💰' },
  { id: 'fase4',     label: 'Fase 4',     emoji: '🔗' },
  { id: 'venda',     label: 'Venda ✅',   emoji: '🏆' },
];

const COLOR_CLASSES: Record<string, { border: string; badge: string; label: string }> = {
  red:    { border: 'border-red-400',    badge: 'bg-red-100 text-red-700',    label: '+24h s/ resposta' },
  yellow: { border: 'border-yellow-400', badge: 'bg-yellow-100 text-yellow-700', label: 'Aguardando resposta' },
  green:  { border: 'border-green-400',  badge: 'bg-green-100 text-green-700',  label: 'Em dia' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Card component ────────────────────────────────────────────────────────────

interface CardProps {
  card: CrmCard;
  onNotesChange: (phone: string, notes: string) => void;
  onFaseChange: (phone: string, fase: string) => void;
  onNameChange: (phone: string, name: string) => void;
}

function KanbanCard({ card, onNotesChange, onFaseChange, onNameChange }: CardProps) {
  const colors = card.color ? (COLOR_CLASSES[card.color] ?? COLOR_CLASSES.green) : null;
  const [editing, setEditing] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [notes, setNotes] = useState(card.notes || '');
  const [tempName, setTempName] = useState('');
  const [moving, setMoving] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const displayName = card.whatsapp_name || card.nome || '—';
  const leadName    = card.nome && card.nome !== card.whatsapp_name ? card.nome : null;

  function saveNotes() {
    setEditing(false);
    if (notes !== card.notes) onNotesChange(card.whatsapp, notes);
  }

  function saveName() {
    setEditingName(false);
    const newName = tempName.trim();
    if (newName && newName !== displayName) {
      onNameChange(card.whatsapp, newName);
    }
  }

  function handleKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setNotes(card.notes || ''); setEditing(false); }
    if (e.key === 'Enter' && e.ctrlKey) saveNotes();
  }

  function handleNameKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { setEditingName(false); }
    if (e.key === 'Enter') { e.preventDefault(); saveName(); }
  }

  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border-l-4 ${colors ? colors.border : 'border-gray-300 dark:border-gray-600'} p-3 text-xs ${!editing && !editingName ? 'cursor-grab active:cursor-grabbing' : ''}`}
      draggable={!editing && !editingName}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', card.whatsapp);
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          {editingName ? (
            <input
              ref={nameRef}
              value={tempName}
              onChange={e => setTempName(e.target.value)}
              onKeyDown={handleNameKey}
              onBlur={saveName}
              autoFocus
              className="w-full text-sm font-semibold border-b border-primary-500 bg-transparent text-gray-900 dark:text-white focus:outline-none"
              placeholder="Nome do lead..."
            />
          ) : (
            <p 
              className="font-semibold text-gray-900 dark:text-white truncate text-sm leading-tight cursor-text hover:text-primary-600 dark:hover:text-primary-400"
              onClick={(e) => {
                e.preventDefault();
                setTempName(displayName === '—' ? '' : displayName);
                setEditingName(true);
              }}
            >
              {displayName}
            </p>
          )}
          {leadName && !editingName && (
            <p className="text-gray-500 dark:text-gray-400 truncate">{leadName}</p>
          )}
        </div>
        {colors && (
          <span className={`ml-2 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${colors.badge}`}>
            {card.color === 'green' ? '✅' : card.color === 'yellow' ? '🟡' : '🔴'}
          </span>
        )}
      </div>

      {/* Meta */}
      <div className="space-y-1 text-gray-500 dark:text-gray-400 mb-2">
        <div className="flex items-center gap-1">
          <span>📱</span>
          <span className="font-mono">{card.whatsapp}</span>
        </div>
        <div className="flex items-center gap-1">
          <span>🕐</span>
          <span>{formatDate(card.data_cadastro)}</span>
        </div>
        {colors && card.color !== 'green' && (
          <div className={`text-[10px] ${colors.badge} px-1.5 py-0.5 rounded`}>
            {colors.label}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="border-t border-gray-100 dark:border-gray-700 pt-2">
        {editing ? (
          <div>
            <textarea
              ref={textRef}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onKeyDown={handleKey}
              rows={3}
              className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded p-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Anotações..."
              autoFocus
            />
            <div className="flex gap-1 mt-1">
              <button
                onClick={saveNotes}
                className="flex-1 bg-primary-600 text-white rounded px-2 py-0.5 text-[10px] hover:bg-primary-700"
              >
                Salvar
              </button>
              <button
                onClick={() => { setNotes(card.notes || ''); setEditing(false); }}
                className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded px-2 py-0.5 text-[10px] hover:bg-gray-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="w-full text-left text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 italic min-h-[20px]"
          >
            {notes || <span className="opacity-50">+ Anotação</span>}
          </button>
        )}
      </div>

      {/* Move to fase */}
      {moving ? (
        <div className="border-t border-gray-100 dark:border-gray-700 pt-2 mt-2">
          <p className="text-[10px] text-gray-500 mb-1">Mover para:</p>
          <div className="flex flex-wrap gap-1">
            {COLUMNS.filter(c => c.id !== card.fase).map(col => (
              <button
                key={col.id}
                onClick={() => { setMoving(false); onFaseChange(card.whatsapp, col.id); }}
                className="bg-gray-100 dark:bg-gray-700 hover:bg-primary-100 dark:hover:bg-primary-900 text-gray-700 dark:text-gray-200 px-1.5 py-0.5 rounded text-[10px]"
              >
                {col.emoji} {col.label}
              </button>
            ))}
          </div>
          <button onClick={() => setMoving(false)} className="text-[10px] text-gray-400 mt-1 hover:text-gray-600">Cancelar</button>
        </div>
      ) : (
        <button
          onClick={() => setMoving(true)}
          className="mt-2 text-[10px] text-gray-400 hover:text-primary-500 border-t border-gray-100 dark:border-gray-700 pt-1 w-full text-left"
        >
          ↕ mover fase
        </button>
      )}
    </div>
  );
}

// ── Board ────────────────────────────────────────────────────────────────────

export default function CRMBoard() {
  const [board, setBoard] = useState<Record<string, CrmCard[]>>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [vendasLoading, setVendasLoading] = useState(false);
  const [showVendas, setShowVendas] = useState(false);

  async function fetchBoard() {
    try {
      const data = await CrmAPI.getBoard();
      setBoard(data.columns);
      setTotal(data.total);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchVendas() {
    setVendasLoading(true);
    try {
      const data = await CrmAPI.getVendas(50);
      setVendas(data);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar vendas');
    } finally {
      setVendasLoading(false);
    }
  }

  useEffect(() => {
    fetchBoard();
    const interval = setInterval(fetchBoard, 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showVendas) {
      fetchVendas();
    }
  }, [showVendas]);

  async function handleNotesChange(phone: string, notes: string) {
    try {
      await CrmAPI.updateNotes(phone, notes);
      // Update local state
      setBoard(prev => {
        const next = { ...prev };
        for (const col of Object.keys(next)) {
          next[col] = next[col].map(c => c.whatsapp === phone ? { ...c, notes } : c);
        }
        return next;
      });
    } catch {
      toast.error('Erro ao salvar anotação');
    }
  }

  async function handleNameChange(phone: string, name: string) {
    try {
      await CrmAPI.updateName(phone, name);
      setBoard(prev => {
        const next = { ...prev };
        for (const col of Object.keys(next)) {
          next[col] = next[col].map(c => c.whatsapp === phone ? { ...c, whatsapp_name: name, nome: name } : c);
        }
        return next;
      });
      toast.success('Nome alterado com sucesso');
    } catch {
      toast.error('Erro ao salvar nome');
    }
  }

  async function handleFaseChange(phone: string, newFase: string) {
    const oldFase = Object.keys(board).find(col => board[col].some(c => c.whatsapp === phone));
    if (!oldFase) return;
    if (oldFase === newFase) return;
    
    const card = board[oldFase].find(c => c.whatsapp === phone);
    if (!card) return;

    // Optimistic update
    setBoard(prev => {
      const next = { ...prev };
      next[oldFase] = next[oldFase].filter(c => c.whatsapp !== phone);
      next[newFase] = [{ ...card, fase: newFase }, ...(next[newFase] || [])];
      return next;
    });

    try {
      await CrmAPI.updateFase(phone, newFase);
      toast.success(`Card movido → ${COLUMNS.find(c => c.id === newFase)?.label}`);
    } catch {
      toast.error('Erro ao mover card');
      // Revert
      fetchBoard();
    }
  }

  async function handleDeleteVenda(vendaId: number) {
    if (!confirm('Tem certeza que deseja apagar esta venda?')) return;
    
    try {
      await CrmAPI.deleteVenda(vendaId);
      setVendas(prev => prev.filter(v => v.id !== vendaId));
      toast.success('Venda removida com sucesso');
    } catch {
      toast.error('Erro ao remover venda');
    }
  }

  const totalByColor = {
    red:    Object.values(board).flat().filter(c => c.color === 'red').length,
    yellow: Object.values(board).flat().filter(c => c.color === 'yellow').length,
  };

  // Filter cards based on search query
  function filterCards(cards: CrmCard[]): CrmCard[] {
    if (!searchQuery.trim()) return cards;
    
    const query = searchQuery.toLowerCase().trim();
    return cards.filter(card => {
      const name = (card.whatsapp_name || card.nome || '').toLowerCase();
      const phone = card.whatsapp.toLowerCase();
      return name.includes(query) || phone.includes(query);
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">📋 CRM Board</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {total} leads ativos · atualiza a cada 30s
            {lastRefresh && ` · ${lastRefresh.toLocaleTimeString('pt-BR')}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {totalByColor.red > 0 && (
            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
              🔴 {totalByColor.red} urgente{totalByColor.red > 1 ? 's' : ''}
            </span>
          )}
          {totalByColor.yellow > 0 && (
            <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
              🟡 {totalByColor.yellow} aguardando
            </span>
          )}
          <button
            onClick={() => { setLoading(true); fetchBoard(); }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            🔄 Atualizar
          </button>
          <button
            onClick={() => setShowVendas(!showVendas)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {showVendas ? '📊 Ocultar Vendas' : '💰 Ver Vendas'}
          </button>
        </div>
      </div>

      {/* Vendas Section */}
      {showVendas && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              💰 Vendas Recentes
              <span className="text-sm font-normal text-gray-500">({vendas.length})</span>
            </h3>
            <button
              onClick={fetchVendas}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              🔄 Atualizar
            </button>
          </div>

          {vendasLoading ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-2xl mb-2">⏳</div>
              <p>Carregando vendas...</p>
            </div>
          ) : vendas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-2xl mb-2">📭</div>
              <p>Nenhuma venda registrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Data</th>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Cliente</th>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Produto</th>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Valor</th>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Origem</th>
                    <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {vendas.map((venda) => {
                    const date = venda.data_hora || venda.created_at;
                    const formattedDate = date ? new Date(date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '—';
                    
                    return (
                      <tr key={venda.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {formattedDate}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">
                          {venda.cliente || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">
                          {venda.produto}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                          R$ {venda.valor_bruto.toFixed(2)}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            venda.status.toLowerCase().includes('pago') || venda.status.toLowerCase().includes('aprovado')
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {venda.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                          {venda.origem || 'Eduzz'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleDeleteVenda(venda.id)}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                            title="Apagar venda"
                          >
                            🗑️ Apagar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="🔍 Buscar por nome ou número..."
          className="w-full px-4 py-2 pl-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            ✕
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
        <span><span className="inline-block w-2 h-2 bg-red-400 rounded-sm mr-1" />+24h sem retorno da admin</span>
        <span><span className="inline-block w-2 h-2 bg-yellow-400 rounded-sm mr-1" />Lead não respondeu ainda</span>
        <span><span className="inline-block w-2 h-2 bg-green-400 rounded-sm mr-1" />Normal</span>
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-gray-500">
            <div className="text-3xl mb-2">⏳</div>
            <p>Carregando board...</p>
          </div>
        </div>
      ) : (
        <div className="flex overflow-x-auto pb-6 gap-3 items-start snap-x custom-scrollbar">
          {COLUMNS.map(col => {
            const cards = board[col.id] || [];
            const filteredCards = filterCards(cards);
            return (
              <div key={col.id} className="flex flex-col min-w-[280px] w-[280px] shrink-0 min-h-[120px] snap-start">
                {/* Column header */}
                <div className="bg-gray-100 dark:bg-gray-800 rounded-t-lg px-3 py-2 flex items-center justify-between sticky top-0 z-10">
                  <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">
                    {col.emoji} {col.label}
                  </span>
                  <span className="bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {filteredCards.length}
                  </span>
                </div>

                {/* Cards */}
                <div 
                  className="bg-gray-50 dark:bg-gray-900 rounded-b-lg p-2 flex flex-col gap-2 flex-1 outline-dashed outline-2 outline-transparent transition-all focus-within:outline-primary-300"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    e.currentTarget.classList.add('bg-primary-50', 'dark:bg-primary-900/20', 'outline-primary-300', 'dark:outline-primary-700');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('bg-primary-50', 'dark:bg-primary-900/20', 'outline-primary-300', 'dark:outline-primary-700');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('bg-primary-50', 'dark:bg-primary-900/20', 'outline-primary-300', 'dark:outline-primary-700');
                    const phone = e.dataTransfer.getData('text/plain');
                    if (phone) {
                      handleFaseChange(phone, col.id);
                    }
                  }}
                >
                  {filteredCards.length === 0 ? (
                    <p className="text-center text-gray-400 dark:text-gray-600 text-xs py-4">
                      {searchQuery ? 'Nenhum resultado' : 'Vazio'}
                    </p>
                  ) : (
                    filteredCards.map(card => (
                      <KanbanCard
                        key={card.whatsapp}
                        card={card}
                        onNotesChange={handleNotesChange}
                        onFaseChange={handleFaseChange}
                        onNameChange={handleNameChange}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
